/**
 * Restricted expression language for preset parameters. No eval / Function — hand-written parser.
 *
 * Grammar:
 *   expr   := term (('+' | '-') term)*
 *   term   := unary (('*' | '/') unary)*
 *   unary  := '-' unary | atom
 *   atom   := NUMBER UNIT? | IDENT | IDENT '(' expr (',' expr)* ')' | '(' expr ')'
 *   UNIT   := 'mm' | 'cm' | 'm'
 * Functions: min, max, round, floor, ceil, abs, clamp(x, lo, hi), roundTo(x, step)
 * Values carry a dimension: 'length' (µm) or 'scalar'.
 */
import { roundHalfAway } from './units.js';

export type Dim = 'length' | 'scalar';

export type Ast =
  | { t: 'num'; value: number; dim: Dim }
  | { t: 'ref'; name: string }
  | { t: 'neg'; arg: Ast }
  | { t: 'bin'; op: '+' | '-' | '*' | '/'; left: Ast; right: Ast }
  | { t: 'call'; fn: FnName; args: Ast[] };

const FUNCTIONS = ['min', 'max', 'round', 'floor', 'ceil', 'abs', 'clamp', 'roundTo'] as const;
type FnName = (typeof FUNCTIONS)[number];
const UNIT_FACTOR: Record<string, number> = { mm: 1000, cm: 10_000, m: 1_000_000 };

export class ExprError extends Error {
  constructor(
    message: string,
    public readonly pos?: number,
  ) {
    super(message);
    this.name = 'ExprError';
  }
}

const MAX_LENGTH = 500;
const MAX_DEPTH = 32;

type Tok = { k: 'num'; v: number; p: number } | { k: 'id'; v: string; p: number } | { k: 'op'; v: string; p: number };

function tokenize(src: string): Tok[] {
  if (src.length > MAX_LENGTH) throw new ExprError('Izraz je predugačak.');
  const out: Tok[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (c === ' ' || c === '\t') {
      i++;
      continue;
    }
    if (/[0-9.]/.test(c)) {
      const m = /^(\d+(\.\d+)?|\.\d+)/.exec(src.slice(i));
      if (!m) throw new ExprError(`Neispravan broj na poziciji ${i}.`, i);
      out.push({ k: 'num', v: Number(m[0]), p: i });
      i += m[0].length;
      continue;
    }
    if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_.]*/.exec(src.slice(i))!;
      out.push({ k: 'id', v: m[0], p: i });
      i += m[0].length;
      continue;
    }
    if ('+-*/(),'.includes(c)) {
      out.push({ k: 'op', v: c, p: i });
      i++;
      continue;
    }
    throw new ExprError(`Nedozvoljen znak '${c}' na poziciji ${i}.`, i);
  }
  return out;
}

export function parseExpr(src: string): Ast {
  const toks = tokenize(src);
  let pos = 0;
  let depth = 0;
  const peek = () => toks[pos];
  const isOp = (v: string) => {
    const t = peek();
    return t?.k === 'op' && t.v === v;
  };
  const expectOp = (v: string) => {
    if (!isOp(v)) throw new ExprError(`Očekivano '${v}'.`, peek()?.p);
    pos++;
  };

  function expr(): Ast {
    if (++depth > MAX_DEPTH) throw new ExprError('Izraz je previše ugniježđen.');
    let left = term();
    while (isOp('+') || isOp('-')) {
      const op = (toks[pos++] as { v: '+' | '-' }).v;
      left = { t: 'bin', op, left, right: term() };
    }
    depth--;
    return left;
  }
  function term(): Ast {
    let left = unary();
    while (isOp('*') || isOp('/')) {
      const op = (toks[pos++] as { v: '*' | '/' }).v;
      left = { t: 'bin', op, left, right: unary() };
    }
    return left;
  }
  function unary(): Ast {
    if (isOp('-')) {
      pos++;
      return { t: 'neg', arg: unary() };
    }
    return atom();
  }
  function atom(): Ast {
    const t = peek();
    if (!t) throw new ExprError('Neočekivan kraj izraza.');
    if (t.k === 'num') {
      pos++;
      const u = peek();
      if (u?.k === 'id' && u.v in UNIT_FACTOR) {
        pos++;
        return { t: 'num', value: t.v * UNIT_FACTOR[u.v]!, dim: 'length' };
      }
      return { t: 'num', value: t.v, dim: 'scalar' };
    }
    if (t.k === 'id') {
      pos++;
      if (isOp('(')) {
        if (!(FUNCTIONS as readonly string[]).includes(t.v)) throw new ExprError(`Nepoznata funkcija '${t.v}'.`, t.p);
        pos++;
        const args: Ast[] = [expr()];
        while (isOp(',')) {
          pos++;
          args.push(expr());
        }
        expectOp(')');
        return { t: 'call', fn: t.v as FnName, args };
      }
      return { t: 'ref', name: t.v };
    }
    if (t.v === '(') {
      pos++;
      const e = expr();
      expectOp(')');
      return e;
    }
    throw new ExprError(`Neočekivan simbol '${t.v}'.`, t.p);
  }

  const ast = expr();
  if (pos !== toks.length) throw new ExprError(`Višak simbola na poziciji ${toks[pos]!.p}.`, toks[pos]!.p);
  return ast;
}

export function collectRefs(ast: Ast, out = new Set<string>()): Set<string> {
  switch (ast.t) {
    case 'ref':
      out.add(ast.name);
      break;
    case 'neg':
      collectRefs(ast.arg, out);
      break;
    case 'bin':
      collectRefs(ast.left, out);
      collectRefs(ast.right, out);
      break;
    case 'call':
      ast.args.forEach((a) => collectRefs(a, out));
      break;
  }
  return out;
}

export interface Value {
  value: number;
  dim: Dim;
}

export function evaluate(ast: Ast, scope: (name: string) => Value | undefined): Value {
  const ev = (a: Ast): Value => {
    switch (a.t) {
      case 'num':
        return { value: a.value, dim: a.dim };
      case 'ref': {
        const v = scope(a.name);
        if (!v) throw new ExprError(`Nepoznat parametar '${a.name}'.`);
        return v;
      }
      case 'neg': {
        const v = ev(a.arg);
        return { value: -v.value, dim: v.dim };
      }
      case 'bin': {
        const l = ev(a.left);
        const r = ev(a.right);
        if (a.op === '+' || a.op === '-') {
          if (l.dim !== r.dim) throw new ExprError('Sabiranje dužine i broja bez jedinice nije dozvoljeno.');
          return { value: a.op === '+' ? l.value + r.value : l.value - r.value, dim: l.dim };
        }
        if (a.op === '*') {
          if (l.dim === 'length' && r.dim === 'length') throw new ExprError('Množenje dvije dužine nije dozvoljeno.');
          return { value: l.value * r.value, dim: l.dim === 'length' || r.dim === 'length' ? 'length' : 'scalar' };
        }
        if (r.value === 0) throw new ExprError('Dijeljenje nulom.');
        if (l.dim === 'scalar' && r.dim === 'length') throw new ExprError('Dijeljenje broja dužinom nije dozvoljeno.');
        return { value: l.value / r.value, dim: l.dim === r.dim ? 'scalar' : 'length' };
      }
      case 'call': {
        const args = a.args.map(ev);
        const same = () => {
          if (args.some((x) => x.dim !== args[0]!.dim)) throw new ExprError(`Argumenti funkcije ${a.fn} moraju imati istu jedinicu.`);
          return args[0]!.dim;
        };
        const arity = (n: number) => {
          if (args.length !== n) throw new ExprError(`Funkcija ${a.fn} očekuje ${n} argument(a).`);
        };
        switch (a.fn) {
          case 'min':
            return { value: Math.min(...args.map((x) => x.value)), dim: same() };
          case 'max':
            return { value: Math.max(...args.map((x) => x.value)), dim: same() };
          case 'abs':
            arity(1);
            return { value: Math.abs(args[0]!.value), dim: args[0]!.dim };
          case 'round':
          case 'floor':
          case 'ceil': {
            arity(1);
            const v = args[0]!;
            // Length rounds to whole mm, scalar to integer.
            const unit = v.dim === 'length' ? 1000 : 1;
            const f = a.fn === 'round' ? roundHalfAway : a.fn === 'floor' ? Math.floor : Math.ceil;
            return { value: f(v.value / unit) * unit, dim: v.dim };
          }
          case 'roundTo': {
            arity(2);
            const [v, step] = args as [Value, Value];
            if (v.dim !== step.dim || step.value <= 0) throw new ExprError('roundTo zahtijeva pozitivan korak iste jedinice.');
            return { value: roundHalfAway(v.value / step.value) * step.value, dim: v.dim };
          }
          case 'clamp': {
            arity(3);
            const dim = same();
            return { value: Math.min(Math.max(args[0]!.value, args[1]!.value), args[2]!.value), dim };
          }
        }
      }
    }
  };
  const result = ev(ast);
  if (!Number.isFinite(result.value)) throw new ExprError('Rezultat nije konačan broj.');
  return result;
}

export interface ParamDef {
  name: string;
  dim: Dim;
  /** Either a literal value (µm for length) or an expression. */
  value?: number;
  expr?: string;
  min?: number;
  max?: number;
  integer?: boolean;
}

export interface ParamEvalResult {
  values: Record<string, number>;
  order: string[];
  errors: { name: string; message: string }[];
}

/** Evaluates parameters in topological order. Cycles and unknown refs are reported, never thrown. */
export function evaluateParams(defs: ParamDef[], external: Record<string, Value> = {}): ParamEvalResult {
  const errors: ParamEvalResult['errors'] = [];
  const byName = new Map(defs.map((d) => [d.name, d]));
  const asts = new Map<string, Ast>();
  const deps = new Map<string, Set<string>>();
  for (const d of defs) {
    if (d.expr === undefined) {
      deps.set(d.name, new Set());
      continue;
    }
    try {
      const ast = parseExpr(d.expr);
      asts.set(d.name, ast);
      deps.set(d.name, new Set([...collectRefs(ast)].filter((r) => byName.has(r))));
    } catch (e) {
      errors.push({ name: d.name, message: (e as Error).message });
      deps.set(d.name, new Set());
    }
  }
  // Kahn's algorithm
  const indeg = new Map<string, number>();
  for (const [n, ds] of deps) indeg.set(n, ds.size);
  const queue = [...indeg].filter(([, v]) => v === 0).map(([n]) => n);
  const order: string[] = [];
  while (queue.length) {
    const n = queue.shift()!;
    order.push(n);
    for (const [m, ds] of deps) {
      if (ds.has(n)) {
        indeg.set(m, indeg.get(m)! - 1);
        if (indeg.get(m) === 0) queue.push(m);
      }
    }
  }
  for (const [n, v] of indeg) {
    if (v > 0) errors.push({ name: n, message: 'Ciklična zavisnost parametara.' });
  }

  const values: Record<string, number> = {};
  const scope = (name: string): Value | undefined => {
    if (name in values) return { value: values[name]!, dim: byName.get(name)!.dim };
    return external[name];
  };
  for (const n of order) {
    const d = byName.get(n)!;
    if (errors.some((e) => e.name === n)) continue;
    let v: number;
    try {
      if (d.expr !== undefined) {
        const r = evaluate(asts.get(n)!, scope);
        if (r.dim !== d.dim) throw new ExprError(`Parametar ${n} očekuje ${d.dim === 'length' ? 'dužinu' : 'broj'}.`);
        v = r.value;
      } else {
        v = d.value ?? NaN;
      }
      if (!Number.isFinite(v)) throw new ExprError('Vrijednost nije definisana.');
      v = d.dim === 'length' || d.integer ? roundHalfAway(v) : v;
      if (d.min !== undefined && v < d.min) throw new ExprError(`Vrijednost ${n} je ispod minimuma.`);
      if (d.max !== undefined && v > d.max) throw new ExprError(`Vrijednost ${n} je iznad maksimuma.`);
      values[n] = v;
    } catch (e) {
      errors.push({ name: n, message: (e as Error).message });
    }
  }
  return { values, order, errors };
}
